import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { fetchCatalog } from '../../store/actions/catalogActions';
import { detailPath } from '../../utils/paths';
import { regionFromPathname } from '../../utils/regionFromPath';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import StaticAdBanner from '../../components/ads/StaticAdBanner';
import AdCarousel from '../../components/ads/AdCarousel';
import AdInline from '../../components/ads/AdInline';
import { bannerAds, carouselAds, cardAds } from '../../data/staticAds';
import { SIMIMOVIES_MANAGED_ADS_ENABLED } from '../../config/ads';
import { useFetchManagedPlacements } from '../../hooks/useFetchManagedPlacements';
import { trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementFirst, selectAdPlacementList } from '../../store/selectors/adSelectors';
import { managedPlacementToAd } from '../../utils/adMapper';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';

const EXPLORE_AD_REQ = Object.freeze([
  { placement: 'inline-banner', limit: 1 },
  { placement: 'results-inline', limit: 1 },
  { placement: 'carousel', limit: 6 },
]);

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 1.25rem;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.crimson};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const H1 = styled.h1`
  margin: 0 0 0.35rem;
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const Lead = styled.p`
  margin: 0 0 2rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  max-width: 40rem;
  line-height: 1.55;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1.25rem;
`;

const Card = styled(motion(Link))`
  text-decoration: none;
  color: inherit;
  border-radius: ${({ theme }) => theme.radiusSm};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  transition: box-shadow 0.2s, transform 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
    transform: translateY(-3px);
  }
`;

const Poster = styled.div`
  aspect-ratio: 2/3;
  background: ${({ theme }) => theme.colors.muted};
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
`;

const Cap = styled.div`
  padding: 0.65rem 0.75rem 0.85rem;
`;

const Name = styled.div`
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  line-height: 1.25;
`;

const Badge = styled.span`
  display: block;
  margin-top: 0.25rem;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.crimson};
`;

export default function ExplorePage() {
  const dispatch = useDispatch();
  const { items: catalogItems, loading } = useSelector((s) => s.catalog);
  const { type } = useParams();
  const { pathname } = useLocation();
  const region = regionFromPathname(pathname);
  const { t } = useI18n();
  const pre = region || '';
  const slug = type || 'movies';
  const exploreGroup = slug === 'anime' ? 'anime' : slug === 'tv' ? 'tv' : 'movies';

  useEffect(() => {
    dispatch(
      fetchCatalog({
        limit: 96,
        group: exploreGroup,
        region: pre || undefined,
      }),
    );
  }, [dispatch, exploreGroup, pre]);

  const items = useMemo(() => {
    return catalogItems || [];
  }, [catalogItems]);

  const title =
    slug === 'movies'
      ? t('explore.movies', 'Movies')
      : slug === 'tv'
        ? t('explore.tv', 'TV shows')
        : slug === 'anime'
          ? t('explore.anime', 'Anime')
          : t('explore.browse', 'Browse');

  const { adDomain, adLocale, adDevice } = useFetchManagedPlacements('explore', EXPLORE_AD_REQ);
  const managedBanner = useSelector((s) => selectAdPlacementFirst(s, 'explore', 'inline-banner'));
  const managedInline = useSelector((s) => selectAdPlacementFirst(s, 'explore', 'results-inline'));
  const managedCarousel = useSelector((s) => selectAdPlacementList(s, 'explore', 'carousel'));

  const exploreBannerAd = useMemo(() => managedPlacementToAd(managedBanner, bannerAds[0]), [managedBanner]);
  const exploreInlineAd = useMemo(() => managedPlacementToAd(managedInline, cardAds[1]), [managedInline]);
  const exploreCarouselAds = useMemo(
    () =>
      managedCarousel.length
        ? managedCarousel.map((x, i) => managedPlacementToAd(x, carouselAds[i] || carouselAds[0]))
        : carouselAds,
    [managedCarousel],
  );

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedBanner?._id) return;
    const key = makeImpressionKey({
      campaign: managedBanner._id,
      page: 'explore',
      placement: 'inline-banner',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedBanner._id,
          page: 'explore',
          placement: 'inline-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedBanner, adDevice, adDomain, adLocale]);

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedInline?._id) return;
    const key = makeImpressionKey({
      campaign: managedInline._id,
      page: 'explore',
      placement: 'results-inline',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedInline._id,
          page: 'explore',
          placement: 'results-inline',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedInline, adDevice, adDomain, adLocale]);

  const onExploreBannerClick = useCallback(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedBanner?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedBanner._id,
        page: 'explore',
        placement: 'inline-banner',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedBanner, adDevice, adDomain, adLocale]);

  const onExploreInlineClick = useCallback(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedInline?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedInline._id,
        page: 'explore',
        placement: 'results-inline',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedInline, adDevice, adDomain, adLocale]);

  const onExploreCarouselVisible = useCallback(
    (visibleAd) => {
      if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !visibleAd?.managedCampaignId) return;
      const key = makeImpressionKey({
        campaign: visibleAd.managedCampaignId,
        page: 'explore',
        placement: 'carousel',
        device: adDevice,
        domain: adDomain,
      });
      if (shouldTrackImpression(key)) {
        dispatch(
          trackAdImpression({
            campaign: visibleAd.managedCampaignId,
            page: 'explore',
            placement: 'carousel',
            device: adDevice,
            domain: adDomain,
            locale: adLocale,
          }),
        );
      }
    },
    [dispatch, adDevice, adDomain, adLocale],
  );

  const onExploreCarouselClick = useCallback(
    (_e, visibleAd) => {
      if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !visibleAd?.managedCampaignId) return;
      dispatch(
        trackAdClick({
          campaign: visibleAd.managedCampaignId,
          page: 'explore',
          placement: 'carousel',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    },
    [dispatch, adDevice, adDomain, adLocale],
  );

  return (
    <PageLayout>
      <Container>
        <Back to={pre ? `/${pre}` : '/'}>
          <ArrowLeft size={18} /> {t('common.home', 'Home')}
        </Back>
        <H1>{title}</H1>
        <Lead>
          {t('explore.lead', 'Browse curated titles for this site and open a card for details, reviews, and similar picks.')}
        </Lead>
        <div style={{ margin: '1.25rem 0' }}>
          <StaticAdBanner ad={exploreBannerAd} variant="inline" pageKey="explore" onLinkClick={onExploreBannerClick} />
        </div>
        <AdInline ad={exploreInlineAd} pageKey="explore" onLinkClick={onExploreInlineClick} />
        <Grid>
          {!loading && items.length === 0 ? (
            <p style={{ color: 'var(--muted-foreground)' }}>{t('explore.empty', 'No titles available yet.')}</p>
          ) : null}
          {items.map((it, i) => (
            <Card
              key={`${it.category}-${it.tmdbId}`}
              to={detailPath(it.category, it.tmdbId, it.title, pre)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.35) }}
            >
              <Poster $url={it.poster} />
              <Cap>
                <Name>{it.title}</Name>
                <Badge>
                  {it.category === 'anime_movie' || it.category === 'anime_tv'
                    ? t('common.anime', 'Anime')
                    : it.category === 'tv'
                      ? t('common.tv', 'TV')
                      : t('common.movie', 'Movie')}
                </Badge>
              </Cap>
            </Card>
          ))}
        </Grid>
        <div style={{ marginTop: '2rem' }}>
          <AdCarousel
            ads={exploreCarouselAds}
            pageKey="explore"
            onVisibleAd={onExploreCarouselVisible}
            onAdClick={onExploreCarouselClick}
          />
        </div>
      </Container>
    </PageLayout>
  );
}
